/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Listbox } from '@headlessui/react';
import { ChevronDownIcon, ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import homeStore from '../store/homeStore';
import { StoreLocation } from '../../../types/common';
import { isMobile } from 'react-device-detect';
import { formatPhoneNumberFlexible } from '../../../utils/tools';

const StoreMap: React.FC = () => {
  const stores = homeStore.storeLocations;
  const [map, setMap] = useState<any | null>(null);
  const [ymapsLoaded, setYmapsLoaded] = useState(false);
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [selectedStore, setSelectedStore] = useState<StoreLocation | null>(null);
  const [isCtrlPressed, setIsCtrlPressed] = useState(false);
  const mapRef = useRef<any>(null);

  // Get unique cities from stores
  const cities = useMemo(() => {
    const uniqueCities = Array.from(new Set(stores.map(store => store.city)));
    return uniqueCities;
  }, [stores]);

  // Set initial city when stores are loaded
  useEffect(() => {
    if (cities.length > 0 && !selectedCity) {
      setSelectedCity(cities[0]);
    }
  }, [cities, selectedCity]);

  // Filter stores by selected city
  const filteredStores = useMemo(() => {
    if (!selectedCity) return stores;
    return stores.filter(store => store.city === selectedCity);
  }, [stores, selectedCity]);

  // Load Yandex Maps API
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://api-maps.yandex.ru/2.1/?apikey=c4338551-9b2b-4fd5-a552-9f6e85ae674b&suggest_apikey=3fdf44f3-77ff-4047-b5ef-4495c4bab4fe&lang=ru_RU';
    script.async = true;
    script.onload = () => {
      // @ts-ignore
      window.ymaps.ready(() => setYmapsLoaded(true));
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Обработчики для клавиши Ctrl
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Control' || e.key === 'Meta') {
        setIsCtrlPressed(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Control' || e.key === 'Meta') {
        setIsCtrlPressed(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Функция для настройки поведения карты
  const configureMapBehavior = (mapInstance: any) => {
    if (!mapInstance) return;

    // Получаем поведение карты
    const behavior = mapInstance.behaviors;
    
    // Отключаем все поведения по умолчанию
    behavior.disable([
      'drag',
      'scrollZoom',
      'dblClickZoom',
      'multiTouch',
      'rightMouseButtonMagnifier',
      'leftMouseButtonMagnifier'
    ]);

    // Включаем перетаскивание всегда
    if(!isMobile) {
    behavior.enable('drag');

    }

    // Включаем зум только при зажатом Ctrl
    if (isCtrlPressed) {
      behavior.enable(['scrollZoom', 'dblClickZoom', 'multiTouch']);
    } else {
      behavior.disable(['scrollZoom', 'dblClickZoom', 'multiTouch']);
    }
  };

  // Initialize map when Yandex Maps is loaded and stores are available
  useEffect(() => {
    if (!ymapsLoaded || !stores || stores.length === 0) return;

    // @ts-ignore
    const ymaps = window.ymaps;

    // Determine which map container to use based on screen size
    const isMobile = window.innerWidth < 1024;
    const mapContainerId = isMobile ? 'map-mobile' : 'map';
    const mapContainer = document.getElementById(mapContainerId);
    
    // On mobile, map container only exists when not viewing store details
    if (!mapContainer) return;

    // Destroy existing map before creating new one
    if (map) {
      map.destroy();
    }

    // Create map instance with custom options
    const newMap = new ymaps.Map(mapContainerId, {
      center: [55.751574, 37.573856],
      zoom: isMobile ? 10 : 5,
      controls: ['zoomControl', 'fullscreenControl'],
      // Настраиваем поведение по умолчанию
      behaviors: ['drag'] // только перетаскивание по умолчанию
    });

    // Добавляем свои обработчики событий для управления зумом
    const handleWheel = (e: WheelEvent) => {
      if (!isCtrlPressed) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    const handleDblClick = (e: MouseEvent) => {
      if (!isCtrlPressed) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    // Добавляем обработчики к контейнеру карты
    mapContainer.addEventListener('wheel', handleWheel, { passive: false });
    mapContainer.addEventListener('dblclick', handleDblClick);

    // Add stores to the map
    stores.forEach(store => {
      if (!store.coordinates) return;

      const placemark = new ymaps.Placemark(
        store.coordinates,
        {
          balloonContent: `
            <div style="padding: 10px;">
              <h3 style="margin: 0 0 10px 0;">${store.city}</h3>
              <p style="margin: 0 0 5px 0;">${store.address}</p>
            </div>
          `
        },
        {
          iconLayout: 'default#image',
          iconImageHref: "/map-location-dot1.png",
          iconImageSize: [35, 35],
          iconImageOffset: [-15, -44]
        }
      );

      newMap.geoObjects.add(placemark);
    });

    // Fit map to show all placemarks
    if (stores.some(store => store.coordinates)) {
      newMap.setBounds(newMap.geoObjects.getBounds(), {
        checkZoomRange: true
      });
    }

    setMap(newMap);
    mapRef.current = newMap;

    // Применяем настройки поведения
    configureMapBehavior(newMap);

    // Cleanup function
    return () => {
      mapContainer.removeEventListener('wheel', handleWheel);
      mapContainer.removeEventListener('dblclick', handleDblClick);
      
      if (newMap) {
        newMap.destroy();
      }
    };
  }, [ymapsLoaded, stores]);

  // Обновляем поведение карты при изменении состояния Ctrl
  useEffect(() => {
    if (map) {
      configureMapBehavior(map);
    }
  }, [isCtrlPressed, map]);

  // Handle city change
  const handleCityChange = (city: string) => {
    setSelectedCity(city);
    setSelectedStore(null);
    
    if (!map || !ymapsLoaded) return;

    // @ts-ignore
    const ymaps = window.ymaps;

    const cityStores = stores.filter(store => store.city === city);
    if (cityStores.length === 0) return;

    ymaps.geocode(city, {
      results: 1
    }).then((res: any) => {
      const firstGeoObject = res.geoObjects.get(0);
      const coords = firstGeoObject.geometry.getCoordinates();
      map.setCenter(coords, 12);
    });
  };

  // Handle store address click
  const handleStoreClick = (store: StoreLocation) => {
    setSelectedStore(store);
    
    if (!map || !store.coordinates) return;

    map.setCenter(store.coordinates, 15, {
      duration: 300
    });
  };

  // Handle back button click
  const handleBackClick = () => {
    setSelectedStore(null);
    
    if (!map || !ymapsLoaded || !selectedCity) return;

    // @ts-ignore
    const ymaps = window.ymaps;

    ymaps.geocode(selectedCity, {
      results: 1
    }).then((res: any) => {
      const firstGeoObject = res.geoObjects.get(0);
      const coords = firstGeoObject.geometry.getCoordinates();
      map.setCenter(coords, 12, {
        duration: 300
      });
    });
  };

  // Show skeleton while stores are loading
  if (!stores || stores.length === 0) {
    return (
      <div className='w-full h-full lg:mt-[15px] flex flex-col lg:flex-row gap-[20px] lg:gap-[30px]'>
        <div className='w-full lg:w-[350px] flex flex-col gap-[20px] lg:h-auto'>
          <h2 className='text-[24px] lg:text-[27px] tracking-[-2%] leading-[100%] text-[#232323] font-bold'>
            Ближайшие магазины
          </h2>
          
          <div className='bg-white shadow-[0_2px_20px_#0000001a] rounded-[20px] p-[20px] lg:p-[24px] lg:h-full flex flex-col gap-[20px] border-gradient'>
            {/* City Selector Skeleton */}
            <div className='flex flex-row items-center gap-[10px]'>
              <div className='h-[20px] w-[60px] bg-gray-200 rounded-[6px] animate-pulse'></div>
              <div className='w-full lg:flex-1 h-[48px] bg-gray-200 rounded-[15px] animate-pulse'></div>
            </div>

            {/* Store List Skeleton */}
            <div className='flex flex-col gap-[15px]'>
              {[1, 2, 3].map((item) => (
                <div key={item} className='flex items-center justify-between h-[18px]'>
                  <div className='h-[15px] w-[70%] bg-gray-200 rounded-[4px] animate-pulse'></div>
                  <div className='w-5 h-5 bg-gray-200 rounded-[4px] animate-pulse'></div>
                </div>
              ))}
            </div>

            {/* Map Skeleton - Mobile Only */}
            <div className='lg:hidden w-full h-[300px] bg-gray-200 rounded-[15px] animate-pulse'></div>
          </div>
        </div>
        
        {/* Map Skeleton - Desktop Only */}
        <div 
          className='hidden lg:block w-full lg:w-[1028px] h-[400px] lg:h-[600px] bg-gray-200 rounded-[20px] animate-pulse'
          style={{ 
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }} 
        />
      </div>
    );
  }

  return (
    <div className='w-full h-full lg:mt-[15px] flex flex-col lg:flex-row gap-[20px] lg:gap-[30px]'>
      <div className='w-full lg:w-[350px] lg:h-auto flex flex-col gap-[20px]'>
        <h2 className='text-[24px] lg:text-[27px] tracking-[-2%] leading-[100%] text-[#232323] font-bold'>
          Ближайшие магазины
        </h2>
        
        <div className='bg-white shadow-[0_2px_20px_#0000001a] rounded-[20px] p-[20px] lg:p-[20px] lg:h-full flex flex-col gap-[20px] border-gradient' id="store-container">
          {selectedStore ? (
            // Store Detail View
            <div className='flex flex-col gap-[15px]'>
              {/* Back button and city name */}
              <div className='flex items-center gap-[10px]'>
                <ChevronLeftIcon 
                  className='w-[20px] h-[20px] text-[#333333] cursor-pointer tracking-[-2%] leading-[100%]' 
                  onClick={handleBackClick}
                />
                <h3 className='text-[20px] font-semibold text-[#333333] tracking-[-2%] leading-[100%]'>
                  {selectedStore.city}
                </h3>
              </div>
              <div className='flex flex-col gap-[20px]'>
                <p className='text-[16px] font-semibold text-[#333333] tracking-[-2%] leading-[100%]'>
                  {selectedStore.address}
                </p>

                {/* Phone section */}
                <div className='flex flex-col gap-[10px]'>
                  <p className='text-[16px] font-semibold text-[#333333] tracking-[-2%] leading-[100%]'>Телефон</p>
                  {selectedStore.phones.map((phone, index) => (
                    <a key={index} className='text-[14px] font-semibold text-[#474A51] tracking-[-2%] leading-[100%] cursor-pointer hover:text-[#232323] transition-colors' href={`tel:${phone}`}>
                      {formatPhoneNumberFlexible(phone)}
                    </a>
                  ))}
                </div>

                {/* Working hours section */}
                <div className='flex flex-col gap-[10px]'>
                  <p className='text-[16px] font-semibold text-[#333333] tracking-[-2%] leading-[100%]'>График работы:</p>
                  
                  {/* Weekdays */}
                  <div className='flex items-center gap-[1px]'>
                    <span className='text-[14px] text-[#474A51] tracking-[-2%] leading-[100%] font-semibold'>Будни</span>
                    <div className='flex-1 self-end border-b border-dotted border-[#CCCCCC]'></div>
                    <span className='text-[14px] text-[#333333] tracking-[-2%] leading-[100%] font-semibold text-right'>{selectedStore.work_hours ?? '9:00 — 19:00'}</span>
                  </div>

                  {/* Weekends */}
                  <div className='flex items-center gap-[1px]'>
                    <span className='text-[14px] text-[#474A51] tracking-[-2%] leading-[100%] font-semibold'>Выходные</span>
                    <div className='flex-1 self-end border-b border-dotted border-[#CCCCCC]'></div>
                    <span className='text-[14px] text-[#333333] tracking-[-2%] leading-[100%] font-semibold text-right'>{selectedStore.work_hours_weekend ?? '9:00 — 19:00'}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Store List View
            <>
              {/* City Selector */}
              <div className='flex flex-row items-center gap-[10px]'>
                <label className='text-[20px] lg:text-[20px] text-[#232323] font-semibold tracking-[-2%] leading-[100%] whitespace-nowrap'>Город</label>
                <Listbox value={selectedCity} onChange={handleCityChange}>
                  <div className='relative w-full'>
                    <Listbox.Button className='w-full lg:flex-1 bg-white border border-[#E5E5E5] !rounded-[15px] !p-[10px] text-left !text-[14px] !font-medium leading-[100%] tracking-[-2%] !text-[#333333] flex items-center justify-between hover:border-[#232323] transition-colors cursor-pointer'>
                      <span>{selectedCity || 'Выберите город'}</span>
                      <ChevronDownIcon className='w-5 h-5 text-[#232323]' />
                    </Listbox.Button>
                    
                    <Listbox.Options className='absolute z-10 w-full mt-[8px] bg-white border border-[#E5E5E5] rounded-[10px] shadow-[0_4px_20px_#0000001a] max-h-[300px] overflow-auto'>
                      {cities.map((city) => (
                        <Listbox.Option
                          key={city}
                          value={city}
                          className={({ active, selected }) =>
                            `px-[16px] py-[12px] cursor-pointer text-[16px] ${
                              active ? 'bg-[#F5F5F5]' : ''
                            } ${selected ? 'font-medium text-[#232323]' : 'text-[#666666]'}`
                          }
                        >
                          {city}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </div>
                </Listbox>
              </div>

              {/* Store List */}
              <div className='flex flex-col gap-[15px]'>
                {filteredStores.map((store) => (
                  <button
                    key={store.id}
                    onClick={() => handleStoreClick(store)}
                    className='w-full flex items-center justify-between min-h-[18px] !p-0 hover:border-[#232323] transition-all text-left group'
                  >
                    <span className='text-[14px] font-semibold tracking-[-2%] leading-[100%] text-[#333333] break-words'>{store.address}</span>
                    <ChevronRightIcon className='w-5 h-5 text-[#666666] group-hover:text-[#232323] transition-colors flex-shrink-0' />
                  </button>
                ))}
              </div>

            </>
          )}
               {/* Map - Mobile Only (inside card) */}
              <div 
                id="map-mobile"
                className='lg:hidden w-full h-[300px] rounded-[15px] overflow-hidden'
                style={{ 
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }} 
              />
        </div>
      </div>
      
      {/* Map - Desktop Only (separate column) */}
      <div 
        id="map" 
        className='hidden lg:block w-full lg:w-[1028px] h-[400px] lg:h-[600px] rounded-[20px]'
        style={{ 
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }} 
      />
      
      {/* Индикатор для пользователя (опционально) */}
      {/* {!isMobile && (
        <div className='fixed bottom-4 right-4 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg text-sm text-gray-700 z-50'>
          {isCtrlPressed ? (
            <span className='flex items-center gap-2'>
              <span className='w-2 h-2 bg-green-500 rounded-full'></span>
              Zoom включен (отпустите Ctrl)
            </span>
          ) : (
            <span className='flex items-center gap-2'>
              <span className='w-2 h-2 bg-gray-400 rounded-full'></span>
              Зажмите Ctrl для зума
            </span>
          )}
        </div>
      )} */}
    </div>
  );
};

export default observer(StoreMap);